import type { AdminTwin } from '@repo/frontend-types';
import type { OnboardingRoute } from '@repo/front-end-utils';
import { readCameraStart } from '@repo/ui';
import { formFromTwin, isLocated, looksLikePlaceholderOrigin } from './twinDetailsForm';
import { positionSourceTag } from './twinValueSources';

/**
 * The *Position on map* step's pure half (first built by `twin-details-page-position-placeholder`,
 * R3): the twin record → the rows frame 10 draws, and the pane's sentence. The step shows every row
 * while it reads; once read, position and orientation are fields
 * (`retire-edit-mode-position-write-path`) and the opening view is its own card
 * (`retire-edit-mode-opening-view`). The Twin details summary's Position card reads all three.
 *
 * Position is judged by the SAME helpers Launch's "where on Earth" check uses (`isLocated`,
 * `looksLikePlaceholderOrigin`), so the step and Launch can never disagree about whether a twin has
 * a position, or whether it is the legacy 51.4779, 0 placeholder.
 *
 * Position carries the source the twin record holds (`position_source`, feature create-rules), and
 * no tag when none is recorded — a twin positioned before sources were recorded, or one whose
 * position changed without naming its source (ruled by maria@twinlabs.ai, 2026-10-16: never guessed).
 * The opening view is *Saved* when the stored view is readable as the viewer reads it
 * (`readCameraStart`), and carries no tag: Position on map writes it too
 * (`retire-edit-mode-opening-view`). Orientation carries no tag: it has no source column, and since
 * `retire-edit-mode-position-write-path` Edit mode is no longer its only writer.
 */

/** The three rows, in frame 10's order — the ONE place their labels live (loading, loaded and the summary card alike). */
export const POSITION_ROWS = [
  { key: 'position', label: 'Position' },
  { key: 'orientation', label: 'Orientation' },
  { key: 'view', label: 'Opening view' },
] as const;

export type PositionRowKey = (typeof POSITION_ROWS)[number]['key'];

export interface PositionRow {
  key: PositionRowKey;
  value: string;
  /** `mint` says where a value came from; `warn` flags the legacy placeholder. */
  tag?: { tone: 'mint' | 'warn'; text: string };
}

/**
 * A stored number shown to at most five decimal places, never padded: `51.524631234` reads
 * `51.52463`, `-0.0838` stays `-0.0838`, `0` stays `0`. Five places is about a metre, which is
 * as fine as a placement read off a map can mean. Anything that is not a finite number is shown as
 * stored rather than guessed at.
 */
export const formatStoredNumber = (raw: string): string => {
  const trimmed = raw.trim();
  const n = Number(trimmed);
  if (trimmed === '' || !Number.isFinite(n)) return trimmed;
  // `Number` drops toFixed's padding and turns -0 into 0; toFixed's exponent form for |n| >= 1e21
  // round-trips intact rather than having zeros stripped from its exponent.
  return String(Number(n.toFixed(5)));
};

export const positionRows = (twin: AdminTwin): PositionRow[] => {
  const form = formFromTwin(twin);

  const position: PositionRow = !isLocated(form)
    ? { key: 'position', value: 'Not on the twin record' }
    : (() => {
        const placeholder = looksLikePlaceholderOrigin(form);
        // The placeholder is shown as stored; a real position to five places (`formatStoredNumber`).
        const value = placeholder
          ? `${form.latitude.trim()}, ${form.longitude.trim()}`
          : `${formatStoredNumber(form.latitude)}, ${formatStoredNumber(form.longitude)}`;
        const tag = positionSourceTag(twin.position_source, placeholder);
        return tag ? { key: 'position', value, tag } : { key: 'position', value };
      })();

  const orientationRaw = twin.orientation == null ? '' : String(twin.orientation).trim();
  const orientation: PositionRow =
    orientationRaw === ''
      ? { key: 'orientation', value: 'Not set' }
      : { key: 'orientation', value: `${formatStoredNumber(orientationRaw)}°` };

  // A stored value the viewer cannot read opens on the default view, so it is not saved either.
  const view: PositionRow =
    readCameraStart(twin.camera_start_args) !== null
      ? { key: 'view', value: 'Saved' }
      : { key: 'view', value: 'Not saved: opens on a default view' };

  return [position, orientation, view];
};

/**
 * The pane's sentence (`retire-edit-mode-position-write-path`, R8). The fields are the twin RECORD's
 * values, and the record is not always what a launch uses: a twin with an established model keeps
 * the origin it is rendered at (`resolveActivationOrigin`, step 1). So a twin that is already live is
 * told the viewer moves but Launch keeps its origin; a new twin, which has no model yet, launches at
 * it (PR review).
 */
export const positionSentence = (route: OnboardingRoute | null): string =>
  `Changes save to the twin straight away. ${
    route === 'manage' || route === 'live-unread' || route === 'migrate'
      ? 'The twin viewer shows it at the new position; Launch keeps the origin a live model is rendered at.'
      : 'The twin launches at this position.'
  }`;
